import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Home, RefreshCw, Plus, Sparkles, Download, Loader2, Layers, Share2, FolderOpen, Crown, LogOut, Check, Flame, CalendarClock, HelpCircle, ZoomIn, ZoomOut } from "lucide-react";
import StartScreen from "./StartScreen";
import FractalGrid from "./FractalGrid";
import FlashcardModal from "./FlashcardModal";
import AnkiExportDialog from "./AnkiExportDialog";
import UpgradeDialog from "./UpgradeDialog";
import ManagePlanDialog from "./ManagePlanDialog";
import HelpDialog from "./HelpDialog";
import CellDetailModal from "./CellDetailModal";
import { Button } from "./ui/button";
import { generateGrid, generateFromNotes, saveMap, shareMap, getDueCards, gradeReview } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MAX_DEPTH = 6;

export default function Explorer({ seed, onOpenMaps }) {
  const { user, refreshUser, logout } = useAuth();
  const isPremium = user?.subscription_tier === "premium";

  const [view, setView] = useState(seed ? "grid" : "start");
  const [cache, setCache] = useState(() => {
    if (!seed) return {};
    const c = {};
    seed.grids.forEach((g) => { c[g.path.join("::")] = g; });
    return c;
  });
  const [path, setPath] = useState(() => (seed ? seed.grids[0].path : []));
  const [rootFromNotes, setRootFromNotes] = useState(seed?.from_notes || false);
  const [loading, setLoading] = useState(false);
  const [studyOpen, setStudyOpen] = useState(false);
  const [ankiOpen, setAnkiOpen] = useState(false);
  const [upgrade, setUpgrade] = useState({ open: false, reason: "" });
  const [autosave, setAutosave] = useState("idle"); // idle | saving | saved
  const [sharing, setSharing] = useState(false);
  const [dueCards, setDueCards] = useState([]);
  const [dueCount, setDueCount] = useState(0);
  const [dueOpen, setDueOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [detailEl, setDetailEl] = useState(null);
  const gridWrapRef = useRef(null);
  const mapIdRef = useRef(seed?.mapId || null);
  const saveTimer = useRef(null);

  const keyOf = (p) => p.join("::");
  const currentGrid = cache[keyOf(path)];

  const branchCards = useMemo(
    () => Object.values(cache)
      .filter((g) => g.path.length >= path.length && path.every((seg, idx) => g.path[idx] === seg))
      .flatMap((g) => g.elements),
    [cache, path]
  );

  const openUpgrade = (reason) => setUpgrade({ open: true, reason });

  const fetchDue = useCallback(async () => {
    try { const d = await getDueCards(); setDueCards(d.cards); setDueCount(d.count); }
    catch (e) { console.warn("fetchDue failed:", e?.message || e); }
  }, []);
  useEffect(() => { fetchDue(); }, [fetchDue]);

  // Ctrl + wheel to zoom the grid (native listener so preventDefault works)
  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(1.8, Math.max(0.6, +(z - Math.sign(e.deltaY) * 0.1).toFixed(2))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [view]);

  const gradeGrid = (card, rating) => {
    gradeReview({ front: card.flashcard_question, back: card.flashcard_answer, deck: keyOf(path), rating }).catch(() => {});
  };
  const gradeDue = (card, rating) => {
    gradeReview({ front: card.flashcard_question, back: card.flashcard_answer, deck: card.deck || "", rating }).catch(() => {});
  };

  const handleQuota = (e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 402) { openUpgrade(detail); return true; }
    toast.error(detail || "Something went wrong. Try again.");
    return false;
  };

  const store = useCallback((grid) => {
    setCache((c) => ({ ...c, [grid.path.join("::")]: grid }));
  }, []);

  const startTopic = async (topic) => {
    setLoading(true);
    try {
      const res = await generateGrid(topic, [topic]);
      store({ topic: res.topic, elements: res.elements, from_notes: false, path: [res.topic] });
      setPath([res.topic]); setRootFromNotes(false); setView("grid");
      refreshUser();
    } catch (e) { handleQuota(e); } finally { setLoading(false); }
  };

  const startNotes = async (file) => {
    setLoading(true);
    try {
      const res = await generateFromNotes(file);
      store({ topic: res.topic, elements: res.elements, from_notes: true, path: [res.topic] });
      setPath([res.topic]); setRootFromNotes(true); setView("grid");
      toast.success(`Extracted "${res.topic}" from your notes`);
      refreshUser();
    } catch (e) { handleQuota(e); } finally { setLoading(false); }
  };

  const drillDown = async (element) => {
    if (path.length >= MAX_DEPTH) { toast.info("Maximum depth of 6 layers reached."); return; }
    const newPath = [...path, element.label];
    if (cache[keyOf(newPath)]) { setPath(newPath); return; }
    setLoading(true);
    try {
      const res = await generateGrid(element.label, newPath);
      store({ topic: res.topic, elements: res.elements, from_notes: false, path: newPath });
      setPath(newPath);
      refreshUser();
    } catch (e) { handleQuota(e); } finally { setLoading(false); }
  };

  const jumpTo = (index) => setPath(path.slice(0, index + 1));

  const regenerate = async () => {
    if (!currentGrid) return;
    setLoading(true);
    try {
      const res = await generateGrid(currentGrid.topic, path, true);
      store({ topic: res.topic, elements: res.elements, from_notes: currentGrid.from_notes, path });
      toast.success("Grid regenerated");
      refreshUser();
    } catch (e) { handleQuota(e); } finally { setLoading(false); }
  };

  const newTopic = () => { setCache({}); setPath([]); setView("start"); mapIdRef.current = null; setAutosave("idle"); setZoom(1); };

  const openStudy = () => {
    if (!isPremium) { openUpgrade("The interactive flashcard viewer is a Premium feature."); return; }
    setStudyOpen(true);
  };

  const saveNow = useCallback(async () => {
    const grids = Object.values(cache);
    if (!grids.length || !path.length) return null;
    const res = await saveMap({ title: path[0], root_topic: path[0], from_notes: rootFromNotes, grids, map_id: mapIdRef.current });
    mapIdRef.current = res.id;
    return res;
  }, [cache, path, rootFromNotes]);

  // Auto-save the explored tree (debounced) whenever it changes.
  useEffect(() => {
    if (view !== "grid" || !Object.keys(cache).length) return;
    setAutosave("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveNow().then(() => setAutosave("saved")).catch(() => setAutosave("idle"));
    }, 900);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [cache, view, saveNow]);

  const doShare = async () => {
    if (!Object.keys(cache).length) return;
    setSharing(true);
    try {
      if (!mapIdRef.current) await saveNow();
      const shareId = await shareMap(mapIdRef.current);
      const link = `${window.location.origin}/api/og/${shareId}`;
      try { await navigator.clipboard.writeText(link); toast.success("Share link copied to clipboard"); }
      catch { toast.success(`Share link: ${link}`); }
    } catch (e) { toast.error("Could not create share link."); } finally { setSharing(false); }
  };

  const usage = user?.usage;
  const limits = user?.limits;

  const openReview = () => {
    if (dueCount === 0) { toast.info("No cards are due for review right now. Study a grid to schedule some!"); return; }
    setDueOpen(true);
  };
  const stripProps = {
    user, isPremium, usage, limits, onOpenMaps, onUpgrade: () => openUpgrade(""),
    onLogout: logout, onHome: newTopic, streak: user?.study_stats?.current_streak || 0,
    dueCount, onReview: openReview, onManage: () => setManageOpen(true), onHelp: () => setHelpOpen(true),
  };

  const sharedModals = (
    <>
      {dueCards.length > 0 && (
        <FlashcardModal open={dueOpen} onOpenChange={(v) => { setDueOpen(v); if (!v) { fetchDue(); refreshUser(); } }}
          cards={dueCards} deckName="Due Review" onGrade={gradeDue} />
      )}
      <ManagePlanDialog open={manageOpen} onOpenChange={setManageOpen} onChanged={refreshUser} />
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      <UpgradeDialog open={upgrade.open} onOpenChange={(v) => setUpgrade({ ...upgrade, open: v })} reason={upgrade.reason} />
    </>
  );

  if (view === "start") {
    return (
      <div className="app-bg min-h-screen">
        <TopStrip {...stripProps} />
        <StartScreen onStartTopic={startTopic} onStartNotes={startNotes} loading={loading} />
        {sharedModals}
      </div>
    );
  }

  const atMaxDepth = path.length >= MAX_DEPTH;
  const allGrids = Object.values(cache);

  return (
    <div className="app-bg min-h-screen">
      <TopStrip {...stripProps}
        breadcrumb={
          <div className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0" data-testid="breadcrumb-trail">
            <Home className="w-3.5 h-3.5 text-purple-400 shrink-0" />
            {path.map((p, idx) => (
              <React.Fragment key={`${p}-${idx}`}>
                {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-purple-500/50 shrink-0" />}
                <button data-testid={`breadcrumb-node-${idx}`} onClick={() => jumpTo(idx)}
                  className={"text-xs px-2 py-1 rounded-md whitespace-nowrap transition-colors " +
                    (idx === path.length - 1 ? "bg-purple-600/25 text-white font-semibold border border-purple-500/40"
                      : "text-purple-200/70 hover:text-white hover:bg-purple-500/10")}>
                  {p}
                </button>
              </React.Fragment>
            ))}
          </div>
        }
      />

      {/* Toolbar */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 flex flex-wrap items-center justify-end gap-2">
        <span data-testid="autosave-indicator" className="mr-auto text-[11px] font-mono-fx text-purple-300/60 flex items-center gap-1.5">
          {autosave === "saving" ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
            : autosave === "saved" ? <><Check className="w-3 h-3 text-emerald-400" /> Auto-saved</> : null}
        </span>
        <Button data-testid="toolbar-share-btn" onClick={doShare} disabled={sharing || !currentGrid} variant="outline"
          className="rounded-full bg-transparent border-purple-500/30 text-purple-200 hover:bg-purple-500/10 hover:text-white">
          {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />} Share
        </Button>
        <Button data-testid="toolbar-regenerate-btn" onClick={regenerate} disabled={loading} variant="outline"
          className="rounded-full bg-transparent border-purple-500/30 text-purple-200 hover:bg-purple-500/10 hover:text-white">
          <RefreshCw className={"w-4 h-4 mr-2 " + (loading ? "animate-spin" : "")} /> Regenerate
        </Button>
        <Button data-testid="toolbar-new-topic-btn" onClick={newTopic} variant="outline"
          className="rounded-full bg-transparent border-purple-500/30 text-purple-200 hover:bg-purple-500/10 hover:text-white">
          <Plus className="w-4 h-4 mr-2" /> New Topic
        </Button>
        <Button data-testid="toolbar-study-btn" onClick={openStudy} disabled={!currentGrid}
          className="rounded-full bg-purple-600/90 hover:bg-purple-500 text-white">
          <Sparkles className="w-4 h-4 mr-2" /> Study This Grid
        </Button>
        <Button data-testid="toolbar-export-anki-btn" onClick={() => setAnkiOpen(true)} disabled={!currentGrid}
          className="rounded-full bg-purple-600 hover:bg-purple-500 text-white shadow-[0_0_20px_rgba(168,85,247,0.4)]">
          <Download className="w-4 h-4 mr-2" /> Export to Anki
        </Button>
      </div>

      {/* Grid */}
      <div ref={gridWrapRef} className="max-w-6xl mx-auto px-4 sm:px-6 py-6 relative overflow-hidden">
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#07070E]/60 backdrop-blur-sm rounded-2xl" data-testid="grid-loading">
            <div className="flex flex-col items-center gap-3 text-purple-200">
              <Loader2 className="w-9 h-9 animate-spin" />
              <p className="text-sm font-mono-fx">Mapping concepts…</p>
            </div>
          </div>
        )}

        {/* Zoom controls */}
        <div className="absolute top-2 right-4 z-20 flex items-center gap-1 rounded-full bg-[#0D0B18]/90 border border-purple-500/25 p-1 backdrop-blur">
          <button data-testid="zoom-out-btn" onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.1).toFixed(2)))}
            className="p-1.5 rounded-full text-purple-200 hover:bg-purple-500/20 disabled:opacity-40" disabled={zoom <= 0.6} title="Zoom out">
            <ZoomOut className="w-4 h-4" />
          </button>
          <button data-testid="zoom-reset-btn" onClick={() => setZoom(1)}
            className="px-2 text-[11px] font-mono-fx text-purple-200 hover:text-white min-w-[44px]" title="Reset zoom">
            {Math.round(zoom * 100)}%
          </button>
          <button data-testid="zoom-in-btn" onClick={() => setZoom((z) => Math.min(1.8, +(z + 0.1).toFixed(2)))}
            className="p-1.5 rounded-full text-purple-200 hover:bg-purple-500/20 disabled:opacity-40" disabled={zoom >= 1.8} title="Zoom in">
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>

        <div style={{ transform: `scale(${zoom})`, transformOrigin: "top center", transition: "transform 0.18s ease-out" }}>
          <AnimatePresence mode="wait">
            {currentGrid && (
              <motion.div key={keyOf(path)}
                initial={{ opacity: 0, scale: 0.96, filter: "blur(6px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, scale: 1.04, filter: "blur(6px)" }}
                transition={{ duration: 0.35 }}>
                <FractalGrid grid={currentGrid} atMaxDepth={atMaxDepth} onCellClick={drillDown} onExpand={setDetailEl} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {atMaxDepth && (
          <p className="text-center mt-4 text-xs text-purple-300/60 font-mono-fx" data-testid="max-depth-note">
            Maximum fractal depth reached — study this layer or jump back via the breadcrumb.
          </p>
        )}
        <p className="text-center mt-3 text-[10px] text-purple-300/40 font-mono-fx">
          Tip: click the ⤢ on a cell for full details · Ctrl + scroll or use +/− to zoom
        </p>
      </div>

      <CellDetailModal open={!!detailEl} onOpenChange={(v) => { if (!v) setDetailEl(null); }}
        element={detailEl} fromNotes={currentGrid?.from_notes} atMaxDepth={atMaxDepth} onDrill={drillDown} />

      {currentGrid && (
        <>
          <FlashcardModal open={studyOpen} onOpenChange={(v) => { setStudyOpen(v); if (!v) { fetchDue(); refreshUser(); } }}
            cards={currentGrid.elements} onGrade={gradeGrid}
            branchCards={branchCards}
            deckName={currentGrid.topic} />
          <AnkiExportDialog open={ankiOpen} onOpenChange={setAnkiOpen} currentGrid={currentGrid}
            allGrids={allGrids} rootName={path[0]} isPremium={isPremium} onUpgrade={openUpgrade} />
        </>
      )}
      {sharedModals}
    </div>
  );
}

function TopStrip({ user, isPremium, usage, limits, onOpenMaps, onUpgrade, onLogout, onHome, breadcrumb, streak, dueCount, onReview, onManage, onHelp }) {
  const meter = isPremium
    ? `Gen ${usage?.generations ?? 0}/${limits?.generations ?? 100}`
    : `Grids ${usage?.root_grids ?? 0}/${limits?.root_grids ?? 3}`;
  return (
    <div className="sticky top-0 z-30 backdrop-blur-xl bg-[#07070E]/80 border-b border-purple-500/15">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <button onClick={onHome} data-testid="brand-home-btn" className="flex items-center gap-2 shrink-0">
          <div className="p-1.5 rounded-lg bg-purple-600/20 border border-purple-500/30">
            <Layers className="w-4 h-4 text-purple-300" />
          </div>
          <span className="font-display font-bold text-white hidden sm:block">FractalNotes</span>
        </button>

        {breadcrumb || <div className="flex-1" />}

        <div className="flex items-center gap-2 shrink-0">
          {streak > 0 && (
            <span data-testid="streak-badge" title={`${streak}-day study streak`}
              className="text-[11px] font-semibold px-2 py-1 rounded-md bg-orange-500/15 border border-orange-400/30 text-orange-300 flex items-center gap-1">
              <Flame className="w-3.5 h-3.5" /> {streak}
            </span>
          )}
          <button data-testid="review-due-btn" onClick={onReview} title="Review due cards"
            className={"text-[11px] font-semibold px-2.5 py-1 rounded-md border flex items-center gap-1 transition-colors " +
              (dueCount > 0 ? "bg-sky-500/15 border-sky-400/30 text-sky-300 hover:bg-sky-500/25"
                : "bg-purple-500/10 border-purple-500/20 text-purple-200/70 hover:text-white")}>
            <CalendarClock className="w-3.5 h-3.5" /> Review{dueCount > 0 ? ` (${dueCount})` : ""}
          </button>
          <span data-testid="usage-meter" className="hidden sm:inline text-[10px] font-mono-fx px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/20 text-purple-200/80">
            {meter}
          </span>
          {isPremium ? (
            <button data-testid="premium-badge" onClick={onManage} title="Manage plan"
              className="text-[10px] px-2 py-1 rounded-md bg-amber-500/15 border border-amber-400/30 text-amber-300 flex items-center gap-1 hover:bg-amber-500/25">
              <Crown className="w-3 h-3" /> Premium
            </button>
          ) : (
            <button data-testid="upgrade-btn" onClick={onUpgrade}
              className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/80 to-purple-600 text-white font-semibold flex items-center gap-1 hover:opacity-90">
              <Crown className="w-3.5 h-3.5" /> Upgrade
            </button>
          )}
          <button data-testid="open-maps-btn" onClick={onOpenMaps}
            className="p-2 rounded-lg text-purple-300/80 hover:text-white hover:bg-purple-500/10" title="My Maps">
            <FolderOpen className="w-4 h-4" />
          </button>
          <button data-testid="help-btn" onClick={onHelp}
            className="p-2 rounded-lg text-purple-300/80 hover:text-white hover:bg-purple-500/10" title="About / Help">
            <HelpCircle className="w-4 h-4" />
          </button>
          <button data-testid="logout-btn" onClick={onLogout}
            className="p-2 rounded-lg text-purple-300/80 hover:text-white hover:bg-purple-500/10" title="Log out">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
